import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useBranding } from "../lib/branding";
import { useNotificationCount } from "../hooks/useNotificationCount";
import {
    Home,
    FolderOpen,
    FileText,
    ParkingSquare,
    Users,
    BarChart3,
    Shield,
    LogOut,
    Menu,
    X,
    MapPin,
    ClipboardList,
    UserCog,
    Bell,
    CalendarDays,
    Globe2,
    BookOpen,
    Rocket,
    Landmark,
    HeartHandshake,
    Wrench,
    ArrowRightLeft,
    CalendarClock,
    ClipboardCheck,
    ChevronDown,
} from "lucide-react";

// Grouped so a volunteer director can scan the menu rather than read a 20-item wall.
// Money goes first: it's the reason the product exists (ROADMAP.md's two visible
// outcomes both live here). Every `to`/`label`/`icon`/`testId`/`roles` value is
// unchanged from the flat list this replaces — only grouping and order changed, so
// frontend/e2e/uat_journeys.py's testId assertions (nav-money, nav-locality,
// nav-guidance, nav-onboarding) keep passing.
const NAV_SECTIONS = [
    {
        section: "Money",
        items: [
            { to: "/money", label: "Where money went", icon: Landmark, testId: "nav-money", roles: ["resident", "household_admin", "estate_admin"] },
            { to: "/pack-score", label: "Pack score", icon: ClipboardCheck, testId: "nav-pack-score", roles: ["resident", "household_admin", "estate_admin"] },
            { to: "/run", label: "Run the estate", icon: Wrench, testId: "nav-run", roles: ["resident", "household_admin", "estate_admin"] },
        ],
    },
    {
        section: "The estate",
        items: [
            { to: "/", label: "Dashboard", icon: Home, testId: "nav-dashboard", roles: null },
            { to: "/cases", label: "Cases", icon: FolderOpen, testId: "nav-cases", roles: null },
            { to: "/dates", label: "Dates & obligations", icon: CalendarClock, testId: "nav-dates", roles: ["estate_admin"] },
            { to: "/documents", label: "Documents", icon: FileText, testId: "nav-documents", roles: null },
            { to: "/calendar", label: "Calendar", icon: CalendarDays, testId: "nav-calendar", roles: null, secondary: true },
            { to: "/admin/map", label: "Estate Map", icon: MapPin, testId: "nav-estate-map", roles: ["estate_admin"], secondary: true },
            { to: "/handover", label: "Handover", icon: ArrowRightLeft, testId: "nav-handover", roles: ["estate_admin"], secondary: true },
        ],
    },
    {
        section: "Your records",
        items: [
            { to: "/notifications", label: "Notifications", icon: Bell, testId: "nav-notifications", roles: null },
            { to: "/privacy", label: "Privacy", icon: Shield, testId: "nav-privacy", roles: ["resident", "household_admin", "estate_admin"], secondary: true },
            { to: "/parking", label: "Parking Assistant", icon: ParkingSquare, testId: "nav-parking", roles: ["resident", "household_admin", "estate_admin"], secondary: true },
            { to: "/locality", label: "Local Library", icon: Globe2, testId: "nav-locality", roles: null, secondary: true },
            { to: "/guidance", label: "Guidance", icon: BookOpen, testId: "nav-guidance", roles: null, secondary: true },
            { to: "/life", label: "Local help", icon: HeartHandshake, testId: "nav-life", roles: null, secondary: true },
        ],
    },
    {
        section: "Admin",
        items: [
            { to: "/onboarding", label: "Set up estate", icon: Rocket, testId: "nav-onboarding", roles: ["estate_admin"] },
            { to: "/admin", label: "Admin", icon: Users, testId: "nav-admin", roles: ["estate_admin"] },
            { to: "/admin/triage", label: "Triage", icon: ClipboardList, testId: "nav-triage", roles: ["estate_admin"] },
            { to: "/admin/privacy", label: "Privacy Requests", icon: UserCog, testId: "nav-admin-privacy", roles: ["estate_admin"] },
            { to: "/audit", label: "Audit Log", icon: Shield, testId: "nav-audit", roles: ["estate_admin"] },
            { to: "/agency", label: "Agency Digest", icon: BarChart3, testId: "nav-agency", roles: ["estate_admin", "agency_viewer"] },
        ],
    },
];

function RoleBadge({ role }) {
    const map = {
        resident: "Resident",
        household_admin: "Household Admin",
        estate_admin: "Estate Admin",
        agency_viewer: "Agency Viewer",
    };
    return (
        <span
            data-testid="user-role-badge"
            className="text-[10px] font-semibold uppercase tracking-widest text-slate-500"
        >
            {map[role] || role}
        </span>
    );
}

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const brand = useBranding();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const notificationCount = useNotificationCount();
    // A section whose items are all filtered out by role renders nothing at all —
    // never an orphan header with no links under it.
    const sections = NAV_SECTIONS.map((s) => ({
        ...s,
        items: s.items.filter((i) => !i.roles || i.roles.includes(user?.role)),
    })).filter((s) => s.items.length > 0);

    return (
        <div className="min-h-screen flex flex-col md:flex-row">
            {/* Top bar (mobile) */}
            <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
                <Link to="/" className="font-heading text-base font-bold text-slate-900" data-testid="brand-link-mobile">
                    {brand.product_name}
                </Link>
                <button
                    type="button"
                    aria-label="Toggle navigation"
                    onClick={() => setOpen(!open)}
                    className="p-2 text-slate-700"
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            {/* Sidebar */}
            <aside
                className={`${open ? "block" : "hidden"} md:block w-full md:w-64 shrink-0 border-r border-slate-200 bg-[#F3F4F6]`}
                data-testid="sidebar"
            >
                <div className="hidden md:block px-6 pt-6 pb-4">
                    <Link to="/" className="font-heading text-lg font-bold tracking-tight text-slate-900" data-testid="brand-link">
                        {brand.product_name}
                    </Link>
                    <div className="text-xs text-slate-500 mt-1" data-testid="brand-tagline">{brand.tagline}</div>
                </div>

                <nav className="px-3 pt-2 pb-4">
                    {sections.map((s) => {
                        const primary = s.items.filter((i) => !i.secondary);
                        const secondary = s.items.filter((i) => i.secondary);
                        const renderItem = (item) => {
                            const Icon = item.icon;
                            return (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.to === "/"}
                                    data-testid={item.testId}
                                    onClick={() => setOpen(false)}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 px-3 py-2 my-0.5 rounded-md text-sm transition-colors duration-150 ${
                                            isActive
                                                ? "bg-white text-slate-900 border border-slate-200"
                                                : "text-slate-700 hover:bg-white/70"
                                        }`
                                    }
                                >
                                    <Icon size={16} />
                                    <span>{item.label}</span>
                                    {item.to === "/notifications" && notificationCount > 0 && (
                                        <span
                                            data-testid="notifications-badge"
                                            className="ml-auto inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold"
                                        >
                                            {notificationCount > 9 ? "9+" : notificationCount}
                                        </span>
                                    )}
                                </NavLink>
                            );
                        };
                        return (
                            <div key={s.section} className="mb-4 last:mb-0">
                                <div
                                    className="px-3 pt-1 pb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400"
                                    data-testid={`nav-section-${s.section.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {s.section}
                                </div>
                                {primary.map(renderItem)}
                                {/* Long-tail items collapse under "More" so the default menu stays
                                    scannable for a volunteer. Items stay mounted (details keeps its
                                    children in the DOM), so role-gating and links are unaffected. */}
                                {secondary.length > 0 && (
                                    <details className="group my-0.5" data-testid={`nav-more-${s.section.toLowerCase().replace(/\s+/g, "-")}`}>
                                        <summary className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-500 hover:bg-white/70 cursor-pointer list-none">
                                            <ChevronDown size={16} className="transition-transform group-open:rotate-180" />
                                            <span>More</span>
                                        </summary>
                                        <div className="mt-0.5">{secondary.map(renderItem)}</div>
                                    </details>
                                )}
                            </div>
                        );
                    })}
                </nav>

                <div className="hidden md:block px-6 pt-4 border-t border-slate-200">
                    <div className="text-sm font-semibold text-slate-900" data-testid="current-user-name">
                        {user?.full_name}
                    </div>
                    <div className="text-xs text-slate-500 mb-1" data-testid="current-user-email">
                        {user?.email}
                    </div>
                    <RoleBadge role={user?.role} />
                    <button
                        type="button"
                        onClick={logout}
                        className="mt-4 flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900"
                        data-testid="logout-button"
                    >
                        <LogOut size={14} /> Sign out
                    </button>
                    {/* Notice must stay reachable after signup, not only shown once at the door —
                        UK GDPR Arts.13-14. */}
                    <div className="mt-3 mb-6 text-[11px] text-slate-400 space-x-2" data-testid="layout-legal-links">
                        <Link to="/privacy-notice" className="hover:text-slate-600 underline">Privacy notice</Link>
                        <span>·</span>
                        <Link to="/terms" className="hover:text-slate-600 underline">Terms</Link>
                    </div>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 px-4 py-6 md:px-8 md:py-8 max-w-full overflow-x-hidden" data-testid="main-content">
                {children}
            </main>
        </div>
    );
}
